import { Minus, Plus, X, Image as ImageIcon } from "lucide-react";

const field = "w-full bg-background border border-input rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block";

function Counter({ value, onChange, min = 0, testid }) {
  const v = Number(value || 0);
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <button type="button" onClick={() => onChange(Math.max(min, v - 1))} className="h-9 w-9 rounded-lg border border-border grid place-items-center hover:bg-muted transition-colors"><Minus className="h-4 w-4" /></button>
      <span className="font-mono-data font-semibold text-lg w-8 text-center">{v}</span>
      <button type="button" onClick={() => onChange(v + 1)} className="h-9 w-9 rounded-lg border border-border grid place-items-center hover:bg-muted transition-colors"><Plus className="h-4 w-4" /></button>
    </div>
  );
}

function FieldRenderer({ f, value, onChange, aiSlot }) {
  const common = { "data-testid": `field-${f.key}` };
  if (f.type === "text") return <input {...common} className={field} value={value || ""} placeholder={f.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "textarea") return <textarea {...common} className={field} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "number") return <input {...common} type="number" className={field} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
  if (f.type === "counter") return <Counter value={value ?? f.default ?? f.min ?? 0} min={f.min ?? 0} onChange={onChange} testid={`counter-${f.key}`} />;
  if (f.type === "select") return (
    <select {...common} className={field} value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">Seleziona...</option>
      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (f.type === "amenities" || f.type === "multiselect") {
    const sel = value || [];
    const toggle = (o) => onChange(sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o]);
    return (
      <div className="flex flex-wrap gap-2" {...common}>
        {(f.options || []).map((o) => (
          <button type="button" key={o} onClick={() => toggle(o)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${sel.includes(o) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary/40"}`}>{o}</button>
        ))}
      </div>
    );
  }
  if (f.type === "list") {
    const items = Array.isArray(value) ? value : [];
    const add = () => onChange([...items, ""]);
    const upd = (i, v) => onChange(items.map((x, idx) => (idx === i ? v : x)));
    const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
      <div className="space-y-2" {...common}>
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input className={field} value={typeof it === "string" ? it : (it?.label || "")} onChange={(e) => upd(i, e.target.value)} placeholder="Regola / voce personalizzata..." />
            <button type="button" onClick={() => rm(i)} className="h-9 w-9 shrink-0 rounded-lg border border-border grid place-items-center text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-sm text-primary font-medium flex items-center gap-1 hover:underline"><Plus className="h-4 w-4" /> Aggiungi voce</button>
      </div>
    );
  }
  if (f.type === "gallery") {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground flex items-center gap-2" {...common}>
        <ImageIcon className="h-4 w-4" /> Le foto si gestiscono nella scheda <strong className="text-foreground">Foto</strong> e vengono inviate automaticamente a Home Smart al salvataggio.
      </div>
    );
  }
  if (f.type === "richtext") return (
    <div>
      {aiSlot}
      <textarea {...common} className={field + " mt-2"} rows={6} value={value || ""} placeholder={f.placeholder || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
  return <input {...common} className={field} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

export default function SchemaForm({ schema, data, onChange, aiSlots = {} }) {
  const set = (key, val) => onChange({ ...data, [key]: val });
  return (
    <div className="space-y-8 divide-y divide-border/60">
      {(schema?.sections || []).map((section) => (
        <div key={section.key} className="pt-8 first:pt-0 space-y-4">
          <h3 className="font-display text-lg font-semibold">{section.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            {(section.fields || []).map((f) => (
              <div key={f.key} className={f.col === 2 ? "md:col-span-2" : ""}>
                <label className={labelCls}>{f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}</label>
                <FieldRenderer f={f} value={data?.[f.key]} onChange={(v) => set(f.key, v)} aiSlot={f.ai ? aiSlots[f.key] : null} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
